<template>
  <div class="page-container animate-fade-in-up">
    <PageHeader
      title="评估报告配置"
      subtitle="在同一个页面里维护报告内容、AI 生成方式和最终展示模块，让管理员用业务语言控制报告结果。"
    />

    <div class="toolbar">
      <el-tabs v-model="activeTab" class="content-tabs" @tab-click="handleTabChange">
        <el-tab-pane label="管理建议" name="1" />
        <el-tab-pane label="成功案例" name="2" />
        <el-tab-pane label="推荐课程" name="3" />
        <el-tab-pane label="AI 大模型设置" name="ai" />
      </el-tabs>
      <el-button
        v-if="isContentTab && $hasPermission('content:create')"
        type="primary"
        icon="el-icon-plus"
        class="btn-click"
        @click="openDialog()"
      >
        新增内容
      </el-button>
    </div>

    <div v-if="isContentTab" class="table-wrapper">
      <el-table :data="filteredContents" stripe class="data-table">
        <el-table-column prop="content_id" label="ID" width="70" />
        <el-table-column label="类别" width="100">
          <template slot-scope="scope">
            <el-tag size="mini" :type="typeTagType(scope.row.type)">{{ typeLabel(scope.row.type) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="内容" min-width="260" show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="content-excerpt">{{ stripHtml(scope.row.content) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="适用风险" width="100">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.risk_level === 0" size="mini">全部</el-tag>
            <el-tag v-else-if="scope.row.risk_level === 1" size="mini" type="success">低风险</el-tag>
            <el-tag v-else-if="scope.row.risk_level === 2" size="mini" type="warning">中风险</el-tag>
            <el-tag v-else size="mini" type="danger">高风险</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="关键词标签" min-width="150">
          <template slot-scope="scope">
            <el-tag
              v-for="(tag, i) in (scope.row.keywords || [])"
              :key="i"
              size="mini"
              type="info"
              style="margin-right: 4px; margin-bottom: 2px;"
            >
              {{ tag }}
            </el-tag>
            <span v-if="!(scope.row.keywords && scope.row.keywords.length)" class="text-muted">—</span>
          </template>
        </el-table-column>
        <el-table-column label="二维码" width="90">
          <template slot-scope="scope">
            <span class="text-muted">{{ scope.row.qr_url ? '已设置' : '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="sort_order" label="优先级" width="80" />
        <el-table-column label="是否开启" width="90">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="small">
              {{ scope.row.status === 1 ? '开启' : '关闭' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150">
          <template slot-scope="scope">
            <el-button v-if="$hasPermission('content:edit')" type="text" class="action-edit" @click="openDialog(scope.row)">编辑</el-button>
            <el-button v-if="$hasPermission('content:delete')" type="text" class="action-disable" @click="deleteContent(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div v-else class="ai-settings-shell">
      <div class="settings-card">
        <div class="section-head">
          <div>
            <div class="section-title">AI 大模型设置</div>
            <div class="section-desc">只展示管理员需要理解的业务能力选项，不展示底层厂商和技术参数。</div>
          </div>
          <el-tag size="small" type="info">当前任务：{{ currentTaskLabel }}</el-tag>
        </div>

        <div class="provider-grid">
          <button
            v-for="provider in aiSettings.providers"
            :key="provider.key"
            type="button"
            class="provider-card"
            :class="{ active: aiSettings.provider === provider.key, disabled: !provider.available }"
            :disabled="!provider.available"
            @click="selectProvider(provider.key)"
          >
            <div class="provider-header">
              <el-tooltip :content="modelAuthorityTip" placement="top" effect="light">
                <span class="provider-name">{{ provider.label }}</span>
              </el-tooltip>
              <el-tag :type="provider.available ? 'success' : 'info'" size="mini">
                {{ provider.available ? '可用' : '暂不可用' }}
              </el-tag>
            </div>
            <div class="provider-desc">{{ provider.description }}</div>
            <div class="provider-hint">{{ provider.status_hint }}</div>
          </button>
        </div>
      </div>

      <div class="settings-card">
        <div class="section-head">
          <div>
            <div class="section-title">提示词配置</div>
            <div class="section-desc">管理员只改业务语言，不直接改底层 JSON 模板，避免把报告结构改坏。</div>
          </div>
          <el-button size="mini" @click="resetPromptSections">恢复整组默认</el-button>
        </div>

        <div v-for="section in promptSectionDefs" :key="section.key" class="prompt-block">
          <div class="prompt-head">
            <label class="prompt-label">{{ section.label }}</label>
            <el-button type="text" @click="resetPromptSection(section.key)">恢复默认</el-button>
          </div>
          <div class="prompt-desc">{{ section.desc }}</div>
          <el-input
            v-model="aiSettings.prompt_sections[section.key]"
            type="textarea"
            :rows="section.rows"
            :placeholder="section.placeholder"
          />
        </div>
      </div>

      <div class="settings-card">
        <div class="section-head">
          <div>
            <div class="section-title">报告显示配置</div>
            <div class="section-desc">这里只控制最终展示哪些模块，不改模块顺序和标题。</div>
          </div>
          <el-button size="mini" @click="resetModules">恢复默认</el-button>
        </div>

        <div class="module-list">
          <div
            v-for="module in moduleDefs"
            :key="module.key"
            class="module-item"
          >
            <div class="module-info">
              <div class="module-name">{{ module.label }}</div>
              <div class="module-desc">{{ module.desc }}</div>
            </div>
            <el-switch v-model="aiSettings.modules[module.key]" />
          </div>
        </div>
      </div>

      <div class="settings-card impact-card">
        <div class="section-title">保存后会影响什么</div>
        <div class="impact-text">{{ aiImpactSummary }}</div>
        <div class="section-actions">
          <el-button type="primary" class="btn-click" :loading="aiSaving" @click="saveAiSettings">
            保存 AI 大模型设置
          </el-button>
          <el-button class="btn-click" @click="resetAiSettings">恢复系统默认</el-button>
        </div>
      </div>

      <div class="settings-card">
        <div class="section-head">
          <div>
            <div class="section-title">验证区</div>
            <div class="section-desc">基于一条已有评估记录实时生成预览，确认配置确实影响结果。</div>
          </div>
          <el-button type="primary" plain :loading="previewLoading" @click="previewAiReport">
            生成预览
          </el-button>
        </div>

        <el-form label-width="90px" class="preview-form">
          <el-form-item label="历史评估">
            <el-select v-model="previewForm.evaluation_id" filterable placeholder="请选择一条历史评估记录" style="width: 100%;">
              <el-option
                v-for="item in previewOptions"
                :key="item.evaluation_id"
                :label="formatPreviewOption(item)"
                :value="item.evaluation_id"
              />
            </el-select>
          </el-form-item>
        </el-form>

        <div v-if="previewResult" class="preview-result">
          <div class="preview-result-head">
            <div class="preview-badges">
              <el-tag :type="previewResult.source === 'ai' ? 'success' : 'warning'" size="small">
                {{ previewResult.source === 'ai' ? '智能生成' : '规则兜底' }}
              </el-tag>
              <el-tooltip :content="modelAuthorityTip" placement="top" effect="light">
                <el-tag size="small">{{ providerLabel(previewResult.provider) }}</el-tag>
              </el-tooltip>
            </div>
            <div class="preview-meta">{{ previewResult.employee_name }} · {{ formatDateTime(previewResult.generated_at) }}</div>
          </div>
          <div class="preview-box">
            <div class="preview-block">
              <div class="preview-title">风险归因分析</div>
              <div class="preview-text">{{ previewRiskSummary }}</div>
            </div>
            <div class="preview-grid">
              <div class="preview-mini-card">
                <div class="mini-label">管理建议</div>
                <div class="mini-value">{{ previewCount('suggestions') }} 条</div>
              </div>
              <div class="preview-mini-card">
                <div class="mini-label">成功案例</div>
                <div class="mini-value">{{ previewCount('cases') }} 条</div>
              </div>
              <div class="preview-mini-card">
                <div class="mini-label">推荐课程</div>
                <div class="mini-value">{{ previewCount('courses') }} 条</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <el-dialog :title="dialog.isEdit ? '编辑内容' : '新增内容'" :visible.sync="dialog.visible" width="680px" :append-to-body="true">
      <el-form :model="dialog.form" label-width="90px">
        <el-form-item label="类别" required>
          <el-select v-model="dialog.form.type" placeholder="选择内容类别" style="width: 100%;">
            <el-option label="管理建议" :value="1" />
            <el-option label="成功案例" :value="2" />
            <el-option label="推荐课程" :value="3" />
          </el-select>
        </el-form-item>
        <el-form-item label="内容" required>
          <SimpleRichEditor v-model="dialog.form.content" />
        </el-form-item>
        <el-form-item v-if="dialog.form.type === 3" label="二维码URL">
          <el-input v-model="dialog.form.qr_url" placeholder="图片链接地址，留空则不在报告中显示二维码" />
        </el-form-item>
        <el-form-item label="适用风险">
          <el-select v-model="dialog.form.risk_level" placeholder="选择风险等级" style="width: 100%;">
            <el-option label="全部适用" :value="0" />
            <el-option label="低风险" :value="1" />
            <el-option label="中风险" :value="2" />
            <el-option label="高风险" :value="3" />
          </el-select>
        </el-form-item>
        <el-form-item label="关联行为项">
          <el-select
            v-model="dialog.form.behavior_tags"
            multiple
            filterable
            placeholder="选择关联的行为项，员工命中时优先匹配本条"
            style="width: 100%;"
          >
            <el-option
              v-for="b in allBehaviors"
              :key="b.behavior_id"
              :label="b.description"
              :value="b.behavior_id"
            />
          </el-select>
          <div class="form-hint">当员工在评估中命中这些行为项时，本条内容将优先进入匹配池</div>
        </el-form-item>
        <el-form-item label="关键词标签">
          <el-select
            v-model="dialog.form.keywords"
            multiple
            filterable
            allow-create
            default-first-option
            placeholder="输入关键词标签，用于前台智能匹配"
            style="width: 100%;"
          >
            <el-option v-for="tag in presetKeywords" :key="tag" :label="tag" :value="tag" />
          </el-select>
          <div class="form-hint">前台将根据员工的行为项标签自动匹配相关内容</div>
        </el-form-item>
        <el-form-item label="优先级">
          <el-input-number v-model="dialog.form.sort_order" :min="1" :max="999" />
          <div class="form-hint">数字越小，优先级越高</div>
        </el-form-item>
        <el-form-item label="是否开启">
          <el-switch v-model="dialog.form.status" :active-value="1" :inactive-value="0" />
          <div class="form-hint">开启表示内容可被 AI 大模型调用；关闭表示仅暂存，不被 AI 大模型调用</div>
        </el-form-item>
        <div class="form-bottom-hint">
          <i class="el-icon-info" style="color: var(--color-accent); margin-right: 4px;" />
          以上内容（关联行为项、关键词标签、适用风险等）均会提供给 AI 大模型，由系统结合员工实际情况进行精准匹配与筛选，最终呈现到报告中。
        </div>
      </el-form>
      <span slot="footer">
        <el-button @click="dialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveContent">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '../../components/PageHeader.vue'
import SimpleRichEditor from '../../components/SimpleRichEditor.vue'

const DEFAULT_MODULES = {
  risk_analysis: true,
  behavior_detail: true,
  suggestions: true,
  cases: true,
  courses: true
}

const EMPTY_PROMPT_SECTIONS = {
  role: '',
  writing: '',
  forbidden: '',
  output: ''
}

const GENERIC_PROVIDER_LABELS = ['骨干保留专家', '骨干保留专家', '骨干保留专家']

export default {
  name: 'ContentManage',
  components: { PageHeader, SimpleRichEditor },
  data() {
    return {
      activeTab: '1',
      dialog: {
        visible: false,
        isEdit: false,
        form: { content_id: null, type: 1, title: '', content: '', qr_url: '', risk_level: 0, keywords: [], behavior_tags: [], content_type: 1, sort_order: 1, status: 1 }
      },
      presetKeywords: ['被忽视', '无发展', '薪资偏低', '工作量大', '沟通少', '外部机会', '绩效下滑', '频繁请假', '晋升失败', '压力大'],
      promptSectionDefs: [
        { key: 'role', label: 'AI角色设定', desc: '告诉 AI 大模型它应该扮演什么样的业务角色。', rows: 3, placeholder: '例如：你是一位资深 HRBP，擅长写保留建议报告。' },
        { key: 'writing', label: '写作要求', desc: '定义语气、长度、重点顺序。', rows: 4, placeholder: '例如：先写结论，再写依据，再给行动建议。' },
        { key: 'forbidden', label: '禁写内容', desc: '明确哪些内容不允许出现。', rows: 3, placeholder: '例如：不要编造公司政策，不要写空话。' },
        { key: 'output', label: '输出格式要求', desc: '用业务语言告诉 AI 大模型每个模块应该怎么写。', rows: 4, placeholder: '例如：风险归因说明主要原因和占比，建议按行动项输出。' }
      ],
      moduleDefs: [
        { key: 'risk_analysis', label: '风险归因分析', desc: '说明员工当前风险的主要来源。' },
        { key: 'behavior_detail', label: '行为明细', desc: '展示本次评估命中的行为项。' },
        { key: 'suggestions', label: '管理建议', desc: '展示系统匹配出的保留行动建议。' },
        { key: 'cases', label: '成功案例', desc: '展示类似情况的保留成功案例。' },
        { key: 'courses', label: '推荐课程', desc: '展示可直接转给主管的课程或资源。' }
      ],
      aiDefaults: {
        provider: 'kimi',
        prompt_sections: { ...EMPTY_PROMPT_SECTIONS },
        modules: { ...DEFAULT_MODULES }
      },
      aiSettings: {
        provider: 'kimi',
        providers: [],
        prompt_sections: { ...EMPTY_PROMPT_SECTIONS },
        modules: { ...DEFAULT_MODULES }
      },
      previewOptions: [],
      previewForm: {
        evaluation_id: null
      },
      previewResult: null,
      saving: false,
      aiSaving: false,
      previewLoading: false,
      pageLoading: false,
      modelAuthorityTip: '基于领先大模型技术，结合三悟骨干保留数据与行为画像，综合研究生成管理建议。'
    }
  },
  computed: {
    currentTaskId() {
      return this.$store.getters.currentTaskId || null
    },
    currentTaskLabel() {
      const task = this.$store.state.tasks.currentTask
      return task && task.task_name ? task.task_name : '平台通用配置'
    },
    isContentTab() {
      return ['1', '2', '3'].includes(this.activeTab)
    },
    contents() {
      const taskId = this.currentTaskId
      const all = this.$store.state.reportContentConfigs.contents || []
      if (!taskId) return all
      return all.filter(c => c.task_id === taskId || !c.task_id)
    },
    filteredContents() {
      const type = parseInt(this.activeTab)
      return this.contents.filter(c => c.type === type).sort((a, b) => a.sort_order - b.sort_order)
    },
    allBehaviors() {
      const list = this.$store.state.behaviors.behaviors || []
      const taskId = this.currentTaskId
      if (!taskId) return list
      return list.filter(b => b.task_id === taskId || !b.task_id)
    },
    aiImpactSummary() {
      const provider = this.providerLabel(this.aiSettings.provider)
      const enabledModules = this.moduleDefs.filter(item => this.aiSettings.modules[item.key]).map(item => item.label)
      return `保存后，新生成的智能建议报告会使用“${provider}”能力，并按你填写的提示词表达；最终只展示这 ${enabledModules.length} 个模块：${enabledModules.join('、') || '无'}。`
    },
    previewRiskSummary() {
      const content = this.previewResult && this.previewResult.content
      return content && content.risk_analysis ? (content.risk_analysis.summary || '暂无风险归因分析') : '暂无风险归因分析'
    }
  },
  created() {
    this.activeTab = this.resolveInitialTab()
    this.loadPageData()
  },
  watch: {
    '$route.query.tab'() {
      this.activeTab = this.resolveInitialTab()
    }
  },
  methods: {
    resolveInitialTab() {
      return this.$route.query.tab === 'ai' ? 'ai' : (['1', '2', '3'].includes(this.$route.query.tab) ? this.$route.query.tab : '1')
    },
    async loadPageData() {
      this.pageLoading = true
      try {
        await Promise.all([
          this.loadContents(),
          this.loadBehaviors(),
          this.loadAiSettings(),
          this.loadPreviewOptions()
        ])
      } finally {
        this.pageLoading = false
      }
    },
    async loadContents() {
      const res = await this.$api.fetchContents(this.currentTaskId ? { task_id: this.currentTaskId } : {})
      this.$store.commit('SET_CONTENTS', res.data || [])
    },
    async loadBehaviors() {
      const res = await this.$api.fetchBehaviors(this.currentTaskId ? { task_id: this.currentTaskId } : {})
      this.$store.commit('SET_BEHAVIORS', res.data || [])
    },
    async loadAiSettings() {
      const res = await this.$api.fetchAiReportSettings(this.currentTaskId ? { task_id: this.currentTaskId } : {})
      if (!res.data) return
      this.aiDefaults = {
        provider: res.data.defaults.provider,
        prompt_sections: { ...res.data.defaults.prompt_sections },
        modules: {
          ...DEFAULT_MODULES,
          ...res.data.defaults.modules
        }
      }
      this.aiSettings = {
        provider: res.data.provider,
        providers: this.sanitizeProviders(res.data.providers || []),
        prompt_sections: { ...EMPTY_PROMPT_SECTIONS, ...(res.data.prompt_sections || {}) },
        modules: {
          ...DEFAULT_MODULES,
          ...(res.data.modules || {})
        }
      }
    },
    async loadPreviewOptions() {
      const res = await this.$api.fetchAdminEvaluations(this.currentTaskId ? { task_id: this.currentTaskId } : {})
      this.previewOptions = (res.data || []).slice(0, 20)
      if (!this.previewForm.evaluation_id && this.previewOptions.length) {
        this.previewForm.evaluation_id = this.previewOptions[0].evaluation_id
      }
    },
    handleTabChange() {
      const query = { ...this.$route.query }
      if (this.activeTab === 'ai') query.tab = 'ai'
      else query.tab = this.activeTab
      this.$router.replace({ path: this.$route.path, query })
    },
    openDialog(row) {
      if (row) {
        this.dialog.isEdit = true
        this.dialog.form = {
          ...row,
          keywords: row.keywords || [],
          behavior_tags: row.behavior_tags || [],
          content_type: row.content_type || 1
        }
      } else {
        this.dialog.isEdit = false
        this.dialog.form = {
          content_id: null,
          type: parseInt(this.activeTab),
          title: '',
          content: '',
          qr_url: '',
          risk_level: 0,
          keywords: [],
          behavior_tags: [],
          content_type: 1,
          sort_order: this.filteredContents.length + 1,
          status: 1,
          task_id: this.currentTaskId
        }
      }
      this.dialog.visible = true
    },
    async saveContent() {
      this.saving = true
      const form = this.dialog.form
      if (!form.content || !form.content.replace(/<[^>]+>/g, '').trim()) {
        this.$message.error('内容不能为空')
        this.saving = false
        return
      }
      const payload = { ...form, title: '' }
      try {
        if (this.dialog.isEdit) {
          const res = await this.$api.updateContent(form.content_id, payload)
          this.$store.commit('UPDATE_CONTENT', res.data)
          this.$message.success('内容已更新')
        } else {
          const res = await this.$api.createContent(payload)
          this.$store.commit('ADD_CONTENT', res.data)
          this.$message.success('内容已新增')
        }
        this.dialog.visible = false
      } catch (e) {
        this.$message.error('保存失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
      } finally {
        this.saving = false
      }
    },
    async deleteContent(row) {
      try {
        await this.$confirm('确定删除此内容？', '提示', { type: 'warning' })
        await this.$api.deleteContent(row.content_id)
        this.$store.commit('DELETE_CONTENT', row.content_id)
        this.$message.success('已删除')
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('删除失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
        }
      }
    },
    selectProvider(key) {
      const provider = this.aiSettings.providers.find(item => item.key === key)
      if (!provider || !provider.available) return
      this.aiSettings.provider = key
    },
    sanitizeProviders(providers) {
      return providers.map((item, index) => ({
        ...item,
        label: GENERIC_PROVIDER_LABELS[index] || '骨干保留专家',
        description: item.available
          ? '基于平台优化后的大数据与 AI 能力，面向人才保留场景生成建议。'
          : '当前方案暂不可用，启用后可参与报告生成。',
        status_hint: item.available
          ? '前台只展示业务能力，不展示底层模型厂商信息。'
          : '待完成接入后即可开放使用。'
      }))
    },
    providerLabel(key) {
      const provider = this.aiSettings.providers.find(item => item.key === key)
      return provider ? provider.label : '骨干保留专家'
    },
    resetPromptSection(key) {
      this.$set(this.aiSettings.prompt_sections, key, this.aiDefaults.prompt_sections[key] || '')
    },
    resetPromptSections() {
      this.aiSettings.prompt_sections = { ...this.aiDefaults.prompt_sections }
    },
    resetModules() {
      this.aiSettings.modules = { ...DEFAULT_MODULES, ...this.aiDefaults.modules }
    },
    resetAiSettings() {
      this.aiSettings.provider = this.aiDefaults.provider
      this.resetPromptSections()
      this.resetModules()
      this.$message.success('已恢复系统默认设置')
    },
    validateAiSettings() {
      return this.promptSectionDefs.every(section => {
        const value = this.aiSettings.prompt_sections[section.key]
        return value && String(value).trim()
      })
    },
    async saveAiSettings() {
      if (!this.validateAiSettings()) {
        this.$message.error('四段提示词都需要填写，不能留空')
        return
      }
      this.aiSaving = true
      try {
        const res = await this.$api.updateAiReportSettings({
          task_id: this.currentTaskId,
          provider: this.aiSettings.provider,
          prompt_sections: this.aiSettings.prompt_sections,
          modules: this.aiSettings.modules
        })
        if (res.data) {
          this.aiDefaults = {
            provider: res.data.defaults.provider,
            prompt_sections: { ...res.data.defaults.prompt_sections },
            modules: { ...DEFAULT_MODULES, ...res.data.defaults.modules }
          }
          this.aiSettings = {
            provider: res.data.provider,
            providers: this.sanitizeProviders(res.data.providers || []),
            prompt_sections: { ...EMPTY_PROMPT_SECTIONS, ...(res.data.prompt_sections || {}) },
            modules: { ...DEFAULT_MODULES, ...(res.data.modules || {}) }
          }
        }
        this.$message.success('AI 大模型设置已保存')
      } catch (e) {
        this.$message.error('保存失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
      } finally {
        this.aiSaving = false
      }
    },
    async previewAiReport() {
      if (!this.previewForm.evaluation_id) {
        this.$message.warning('请先选择一条历史评估记录')
        return
      }
      this.previewLoading = true
      try {
        const res = await this.$api.previewAiReportSettings({
          evaluation_id: this.previewForm.evaluation_id,
          provider: this.aiSettings.provider,
          prompt_sections: this.aiSettings.prompt_sections,
          modules: this.aiSettings.modules
        })
        this.previewResult = res.data
        this.$message.success(this.previewResult.source === 'ai' ? '预览已生成' : '智能生成不可用，已展示规则兜底结果')
      } catch (e) {
        this.$message.error('预览失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
      } finally {
        this.previewLoading = false
      }
    },
    previewCount(key) {
      const content = this.previewResult && this.previewResult.content
      const items = content && content[key] && content[key].items
      return Array.isArray(items) ? items.length : 0
    },
    formatPreviewOption(item) {
      return `${item.employee_name || '未知员工'}｜${this.formatDateTime(item.created_at)}｜${this.riskLabel(item.risk_level)}`
    },
    formatDateTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return String(value).replace('T', ' ').split('.')[0]
      }
      const pad = num => String(num).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    riskLabel(level) {
      return level === 3 ? '高风险' : (level === 2 ? '中风险' : '低风险')
    },
    typeLabel(type) {
      const map = { 1: '管理建议', 2: '成功案例', 3: '推荐课程' }
      return map[type] || '未知'
    },
    typeTagType(type) {
      const map = { 1: 'primary', 2: 'success', 3: 'warning' }
      return map[type] || ''
    },
    stripHtml(html) {
      if (!html) return ''
      return html.replace(/<[^>]+>/g, '')
    }
  }
}
</script>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-lg);
}

.content-tabs {
  flex: 1;
}

.content-tabs >>> .el-tabs__header {
  margin-bottom: 0;
}

.content-tabs >>> .el-tabs__nav-wrap::after {
  height: 1px;
  background-color: var(--color-border);
}

.table-wrapper,
.settings-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.table-wrapper {
  overflow: hidden;
}

.data-table >>> th {
  background: var(--color-bg) !important;
  color: var(--color-text) !important;
  font-weight: 600 !important;
}

.action-edit {
  color: var(--color-primary) !important;
}

.action-disable {
  color: var(--color-high) !important;
}

.text-muted {
  color: var(--color-text-muted);
}

.content-excerpt {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ai-settings-shell {
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
}

.settings-card {
  padding: var(--space-2xl);
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-lg);
  margin-bottom: var(--space-lg);
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
}

.section-desc {
  margin-top: 4px;
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.7;
}

.provider-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-md);
}

.provider-card {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg);
  padding: var(--space-lg);
  text-align: left;
  cursor: pointer;
  transition: all var(--duration-fast) var(--ease-out);
}

.provider-card:hover:not(.disabled) {
  border-color: var(--color-primary);
  transform: translateY(-2px);
}

.provider-card.active {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(255, 196, 0, 0.18);
}

.provider-card.disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.provider-header {
  display: flex;
  justify-content: space-between;
  gap: var(--space-sm);
  margin-bottom: var(--space-sm);
}

.provider-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text);
}

.provider-desc,
.provider-hint {
  font-size: 12px;
  color: var(--color-text-secondary);
  line-height: 1.7;
}

.provider-hint {
  margin-top: var(--space-sm);
}

.prompt-block + .prompt-block {
  margin-top: var(--space-xl);
}

.prompt-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.prompt-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
}

.prompt-desc {
  font-size: 12px;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-sm);
  line-height: 1.7;
}

.module-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}

.module-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-lg);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  padding: var(--space-lg);
  background: var(--color-bg);
}

.module-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
}

.module-desc {
  margin-top: 4px;
  font-size: 12px;
  color: var(--color-text-secondary);
  line-height: 1.6;
}

.impact-card {
  background: linear-gradient(135deg, #fffaf0 0%, #fff 100%);
}

.impact-text {
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.9;
}

.section-actions {
  display: flex;
  gap: var(--space-md);
  margin-top: var(--space-lg);
}

.preview-form {
  margin-bottom: var(--space-lg);
}

.preview-result {
  border-top: 1px solid var(--color-border-light);
  padding-top: var(--space-lg);
}

.preview-result-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  margin-bottom: var(--space-md);
}

.preview-badges {
  display: flex;
  gap: var(--space-sm);
}

.preview-meta {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.preview-box {
  background: var(--color-bg);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  padding: var(--space-lg);
}

.preview-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: var(--space-sm);
}

.preview-text {
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.8;
}

.preview-grid {
  margin-top: var(--space-lg);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: var(--space-md);
}

.preview-mini-card {
  padding: var(--space-md);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
}

.mini-label {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.mini-value {
  margin-top: 6px;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
}

.form-hint {
  margin-top: 6px;
  font-size: 12px;
  color: var(--color-text-secondary);
  line-height: 1.6;
}

.form-bottom-hint {
  font-size: 12px;
  color: var(--color-text-secondary);
  line-height: 1.6;
  background: var(--color-bg);
  padding: 10px 12px;
  border-radius: var(--radius-md);
  margin-top: 4px;
}

@media (max-width: 768px) {
  .toolbar,
  .section-head,
  .preview-result-head {
    flex-direction: column;
    align-items: stretch;
  }

  .provider-grid,
  .preview-grid {
    grid-template-columns: 1fr;
  }
}
</style>
